import React from "react";
import "./YouTubePlayer.scss";

interface YouTubePlayerProps {
  url: string;
  autoplay?: boolean;
  width?: string | number;
  height?: string | number;
  aspectRatio?: string | null;
  title?: string;
  allowFullScreen?: boolean;
  errorFallback?: React.ReactNode;
  className?: string;
}

/**
 * Extracts YouTube video ID from various URL formats
 * Supports:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 */
const extractVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
    /^([a-zA-Z0-9_-]{11})$/, // Direct video ID
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
};

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  url,
  autoplay = false,
  width = "100%",
  height = 360,
  aspectRatio = null,
  title = "YouTube video player",
  allowFullScreen = true,
  errorFallback = null,
  className = "",
}) => {
  const videoId = extractVideoId(url);

  if (!videoId) {
    if (errorFallback) {
      return <>{errorFallback}</>;
    }
    // Else default error message
    return (
      <div
        className={`youtube-player-error ${className}`}
        style={{ width: width, height: height }}
      >
        <p>Invalid YouTube URL</p>
      </div>
    );
  }

  const embedUrl = `https://www.youtube.com/embed/${videoId}${
    autoplay ? "?autoplay=1" : ""
  }`;

  const iframeStyle: React.CSSProperties = {
    width: typeof width === "number" ? `${width}px` : width,
  };

  // Use aspect-ratio if provided, otherwise use height
  if (aspectRatio) {
    iframeStyle.aspectRatio = aspectRatio;
  } else {
    iframeStyle.height = typeof height === "number" ? `${height}px` : height;
  }

  return (
    <iframe
      style={iframeStyle}
      src={embedUrl}
      title={title}
      frameBorder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen={allowFullScreen}
      className={className}
    />
  );
};

export default YouTubePlayer;
